import React from 'react';
import clsx from 'clsx';
import { format } from 'date-fns';
import { formatPrice } from '../../../state/offerings';
import { useBrandTheme, getCtaClasses } from '../brand-theme';
import type { BookingSummaryProps, BookingStepV4 } from '../types';

/**
 * Desktop booking summary sidebar.
 * Sticky component that shows live booking details and contextual CTA.
 * 
 * Uses brand theme colors from context for CTA and accents.
 */
export const BookingSummary: React.FC<BookingSummaryProps> = ({
  selectedPackage,
  selectedRooms,
  dateRange,
  guestCounts,
  pricing,
  currentStep,
  onProceed,
  canProceed,
  messages,
  getTentConfig,
}) => {
  const { theme } = useBrandTheme();
  const hasRooms = selectedRooms.length > 0;
  const hasDates = dateRange.start && dateRange.end;
  const totalRooms = selectedRooms.reduce((sum, [, count]) => sum + count, 0);
  const totalGuests = guestCounts.adults + guestCounts.children;

  // Get contextual CTA text based on current step
  const ctaText = getCtaText(currentStep, canProceed, messages);

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-md-tent overflow-hidden">
      {/* Header with brand logo area */}
      <div className="px-5 py-4 border-b border-dashed border-gray-300 flex items-center justify-center">
        <div className="border-b border-dashed border-gray-300 flex-1" />
        <h3 className="font-serif font-semibold text-gray-700 uppercase tracking-wider text-sm px-4">
          Your booking
        </h3>
        <div className="border-b border-dashed border-gray-300 flex-1" />
      </div>

      {/* Content */}
      <div className="p-5 space-y-5">
        {/* Package (if selected) */}
        {selectedPackage && (
          <SummaryRow
            icon={<PackageIcon className="w-5 h-5" />}
            label={messages.packageLabel}
            value={
              <span className={clsx('font-medium', theme.accentText)}>{selectedPackage.name}</span>
            }
          />
        )}

        {/* Rooms */}
        <SummaryRow
          icon={<BedIcon className="w-5 h-5" />}
          label={messages.roomsLabel}
          value={
            hasRooms ? (
              <div className="space-y-1.5">
                {selectedRooms.map(([type, count]) => {
                  const config = getTentConfig?.(type);
                  return (
                    <div key={type} className="flex items-center gap-2">
                      <span className={clsx('font-mono font-semibold', theme.accentText)}>{count}×</span>
                      <span className="text-gray-700">{config?.title || type}</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <span className="text-gray-400 italic">{messages.selectRooms}</span>
            )
          }
        />

        {/* Dates */}
        <SummaryRow
          icon={<CalendarIcon className="w-5 h-5" />}
          label={messages.datesLabel}
          value={
            hasDates ? (
              <span className="text-gray-700">
                {format(new Date(dateRange.start), 'MMM d')} –{' '}
                {format(new Date(dateRange.end), 'MMM d, yyyy')}
              </span>
            ) : (
              <span className="text-gray-400 italic">{messages.selectDates}</span>
            )
          }
        />

        {/* Guests */}
        <SummaryRow
          icon={<UsersIcon className="w-5 h-5" />}
          label={messages.guestsLabel}
          value={
            totalGuests > 0 ? (
              <span className="text-gray-700">
                {guestCounts.adults} {guestCounts.adults === 1 ? messages.adult : messages.adults}
                {guestCounts.children > 0 && (
                  <>
                    , {guestCounts.children}{' '}
                    {guestCounts.children === 1 ? messages.child : messages.children}
                  </>
                )}
              </span>
            ) : (
              <span className="text-gray-400">–</span>
            )
          }
        />
      </div>

      {/* Pricing section */}
      <div className="px-5 pb-5">
        <div className="border-t border-dashed border-gray-300 pt-4">
          {/* Price breakdown */}
          {pricing.total > 0 ? (
            <div className="space-y-2.5 font-mono">
              {/* Accommodation cost */}
              {pricing.accommodationTotal > 0 && pricing.nights > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 font-sans">
                    {totalRooms} {totalRooms === 1 ? 'room' : 'rooms'} × {pricing.nights}{' '}
                    {pricing.nights === 1 ? messages.night : messages.nights}
                  </span>
                  <span className="text-gray-700 font-medium">
                    {formatPrice({ amount: pricing.accommodationTotal, currency: 'INR' })}
                  </span>
                </div>
              )}

              {/* Accommodation GST */}
              {pricing.accommodationGst > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 font-sans pl-3">
                    GST ({pricing.gstPercentage}%)
                  </span>
                  <span className="text-gray-600">
                    {formatPrice({ amount: pricing.accommodationGst, currency: 'INR' })}
                  </span>
                </div>
              )}

              {/* Package total (aggregated - no per-item breakdown) */}
              {selectedPackage && pricing.packageTotal > 0 && (
                <>
                  <div className="flex justify-between text-sm pt-2 mt-2 border-t border-dotted border-gray-200">
                    <span className={clsx('text-gray-700 font-sans font-medium', theme.accentText)}>
                      {selectedPackage.name}
                    </span>
                    <span className="text-gray-700 font-medium">
                      {formatPrice({ amount: pricing.packageTotal, currency: 'INR' })}
                    </span>
                  </div>

                  {/* Package GST */}
                  {pricing.packageGst > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500 font-sans pl-3">
                        GST ({pricing.packageGstPercentage}%)
                      </span>
                      <span className="text-gray-600">
                        {formatPrice({ amount: pricing.packageGst, currency: 'INR' })}
                      </span>
                    </div>
                  )}
                </>
              )}

              {/* Total */}
              <div className="flex justify-between pt-3 mt-2 border-t border-dotted border-gray-400">
                <span className="font-serif font-semibold text-gray-700 uppercase tracking-wider text-sm">
                  {messages.grandTotalLabel}
                </span>
                <span className="font-bold text-xl text-gray-700">
                  {formatPrice({ amount: pricing.totalWithGst, currency: 'INR' })}
                </span>
              </div>
            </div>
          ) : (
            <div className="text-center py-3">
              <p className="text-gray-400 text-sm">Select rooms and dates to see pricing</p>
            </div>
          )}
        </div>
      </div>

      {/* CTA Button - uses brand theme */}
      <div className="p-5 pt-0">
        <button
          type="button"
          onClick={onProceed}
          disabled={!canProceed}
          className={clsx(
            'w-full py-4 px-6 rounded-md font-sans font-bold text-sm uppercase tracking-widest',
            'transition-all duration-200',
            'focus:outline-none focus:ring-2 focus:ring-offset-2',
            getCtaClasses(theme, canProceed),
          )}
        >
          {ctaText}
        </button>
      </div>
    </div>
  );
};

interface SummaryRowProps {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
}

const SummaryRow: React.FC<SummaryRowProps> = ({ icon, label, value }) => (
  <div className="flex items-start gap-3.5">
    <div className="text-gray-500 mt-0.5 flex-shrink-0">{icon}</div>
    <div className="flex-1 min-w-0">
      <p className="text-xs font-serif text-gray-500 uppercase tracking-wider mb-1">
        {label}
      </p>
      <div className="text-sm">{value}</div>
    </div>
  </div>
);

function getCtaText(
  step: BookingStepV4,
  canProceed: boolean,
  messages: BookingSummaryProps['messages'],
): string {
  if (!canProceed) {
    switch (step) {
      case 'room-selection':
        return messages.selectRooms;
      case 'date-guest':
        return messages.selectDates;
      case 'guest-details':
        return messages.enterDetails;
      default:
        return messages.continueButton;
    }
  }

  if (step === 'guest-details') {
    return messages.proceedToPayment;
  }

  return messages.continueButton;
}

// Icon components
const PackageIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
    />
  </svg>
);

const BedIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
    />
  </svg>
);

const CalendarIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
    />
  </svg>
);

const UsersIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
    />
  </svg>
);

export default BookingSummary;
